/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ActionButton, IButtonProps } from '@fluentui/react/lib/Button';
import { IIconProps, Icon } from '@fluentui/react/lib/Icon';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import pick from 'lodash/pick';
import * as React from 'react';
import { getStylist } from 'src/core/themes/luisStylist';

const { styleDiv, styleComponent } = getStylist('Collapsible');

const TitleWrapper = styleComponent<IStackProps>(Stack)('TitleWrapper', {
    alignItems: 'center',
    height: '100%'
});

const TitleContainer = styleDiv('TitleContainer', {
    flex: 1,
    marginRight: '8px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
});

const ActionLink = styleComponent<IButtonProps>(ActionButton)('ActionLink', theme => ({
    paddingLeft: 0,
    marginLeft: 0,
    fontSize: theme.titleFontSize,
    $nest: {
        '& .ms-Button-label': {
            paddingLeft: 0,
            marginLeft: 0
        },
        '&:not([disabled])': {
            color: theme.actionColor
        }
    }
}));

const ActionIcon = styleComponent<IIconProps>(Icon)('ActionIcon', theme => ({
    fontSize: theme.iconSize
}));

const Content = styleDiv<{ collapsed: boolean }>(
    'Content',
    {
        width: '100%',
        height: 'auto',
        transition: 'all 250ms ease',
        overflowY: 'auto'
    },
    ({ customProps: { collapsed } }) => ({
        maxHeight: collapsed ? '0px' : '500px'
    })
);

type HeaderProps = {
    /**
     * Callback for when the collapsible title is clicked.
     */
    onTitleClick?: () => void;
    /**
     * Collapsible title text.
     */
    title: string;
    /**
     * Whether the collapsible is disabled or not.
     */
    disabled?: boolean;
    /**
     * Collapsible state: expanded or collapsed.
     */
    collapsed?: boolean;
};

export type CollapsibleProps = HeaderProps & {
    /**
     * Classname for the root element.
     */
    className?: string;
    /**
     * Collapsible content.
     */
    children?: React.ReactNode;
};

const Header = (props: HeaderProps) => {
    const { title, onTitleClick, disabled = false, collapsed } = props;

    return (
        <ActionLink disabled={disabled} onClick={onTitleClick}>
            <TitleWrapper horizontal>
                <TitleContainer>{title}</TitleContainer>
                <ActionIcon iconName={collapsed ? 'chevronRight' : 'chevronDown'} />
            </TitleWrapper>
        </ActionLink>
    );
};

export const Collapsible = (props: CollapsibleProps) => {
    const { children, collapsed = true, className = '' } = props;
    const headerProps = pick(props, 'title', 'onTitleClick', 'disabled', 'collapsed');

    return (
        <div className={className}>
            <Header {...headerProps} />
            <Content customProps={{ collapsed }}>{children}</Content>
        </div>
    );
};
